/**
 * Patches console methods once per page load so the hub support form can attach recent output.
 * Also records failed fetch responses, window errors, and unhandled promise rejections.
 * Ring buffer only; does not send data until the user submits a ticket.
 */

export const HUB_SUPPORT_EMPTY_CONSOLE_MESSAGE =
  "(No console output captured yet in this tab.)";

const MAX_LINES = 450;
const MAX_FETCH_BODY_CHARS = 900;
const lines: string[] = [];
let installed = false;

/** Test-only reset; clears the ring buffer without uninstalling hooks. */
export function resetHubSupportConsoleCaptureForTests(): void {
  lines.length = 0;
}

function stringifyArg(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value);
  }
  if (value instanceof Error) {
    return value.stack ?? value.message;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function formatLine(level: string, args: unknown[]): string {
  const ts = new Date().toISOString();
  const body = args.map(stringifyArg).join(" ");
  return `[${ts}] ${level} ${body}`;
}

function pushLine(text: string): void {
  lines.push(text);
  if (lines.length > MAX_LINES) {
    lines.splice(0, lines.length - MAX_LINES);
  }
}

/** Record a line for hub support capture without going through patched console methods. */
export function recordHubSupportConsoleLine(level: string, ...args: unknown[]): void {
  pushLine(formatLine(level, args));
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

/** Apache / gateway error pages arrive as an HTML document, not useful log text. */
function looksLikeHtmlDocument(text: string): boolean {
  const t = text.trimStart();
  return /^<!doctype\s+html/i.test(t) || /^<html[\s>]/i.test(t);
}

async function logFailedFetchResponse(response: Response): Promise<void> {
  try {
    const clone = response.clone();
    const text = await clone.text();
    if (looksLikeHtmlDocument(text)) {
      // Collapse full gateway HTML pages (e.g. Apache 503 during a deploy) so the
      // support buffer stays readable instead of containing a whole HTML document.
      pushLine(
        formatLine("error", [
          `HTTP ${response.status} ${response.url}: <gateway HTML page suppressed (hub likely updating)>`,
        ]),
      );
      return;
    }
    const snippet =
      text.length > MAX_FETCH_BODY_CHARS
        ? `${text.slice(0, MAX_FETCH_BODY_CHARS)}…`
        : text;
    pushLine(
      formatLine("error", [`HTTP ${response.status} ${response.url}: ${snippet}`]),
    );
  } catch {
    pushLine(formatLine("error", [`HTTP ${response.status} ${response.url}`]));
  }
}

export function installHubSupportConsoleCapture(): void {
  if (installed) return;
  installed = true;
  const c = globalThis.console;
  const origLog = c.log.bind(c);
  const origInfo = c.info.bind(c);
  const origWarn = c.warn.bind(c);
  const origError = c.error.bind(c);
  const origDebug = c.debug.bind(c);

  c.log = (...args: unknown[]) => {
    pushLine(formatLine("log", args));
    origLog(...args);
  };
  c.info = (...args: unknown[]) => {
    pushLine(formatLine("info", args));
    origInfo(...args);
  };
  c.warn = (...args: unknown[]) => {
    pushLine(formatLine("warn", args));
    origWarn(...args);
  };
  c.error = (...args: unknown[]) => {
    pushLine(formatLine("error", args));
    origError(...args);
  };
  c.debug = (...args: unknown[]) => {
    pushLine(formatLine("debug", args));
    origDebug(...args);
  };

  if (typeof globalThis.addEventListener === "function") {
    globalThis.addEventListener("error", (event) => {
      const detail =
        event.error instanceof Error
          ? (event.error.stack ?? event.error.message)
          : event.message;
      pushLine(formatLine("error", [`window.error ${event.filename}:${event.lineno}: ${detail}`]));
    });

    globalThis.addEventListener("unhandledrejection", (event) => {
      const reason = event.reason;
      const detail =
        reason instanceof Error ? (reason.stack ?? reason.message) : stringifyArg(reason);
      pushLine(formatLine("error", [`unhandledrejection: ${detail}`]));
      event.preventDefault();
    });
  }

  if (typeof globalThis.fetch !== "function") {
    return;
  }
  const origFetch = globalThis.fetch.bind(globalThis);
  globalThis.fetch = async (input, init) => {
    const url = requestUrl(input);
    try {
      const response = await origFetch(input, init);
      if (!response.ok && !url.includes("/api/")) {
        void logFailedFetchResponse(response);
      }
      return response;
    } catch (error) {
      const detail = error instanceof Error ? error.message : stringifyArg(error);
      pushLine(formatLine("error", [`fetch failed ${url}: ${detail}`]));
      throw error;
    }
  };
}

/** Preview text for the support modal (includes empty-state hint). */
export function readHubSupportConsoleCapturePreview(): string {
  if (lines.length === 0) {
    return HUB_SUPPORT_EMPTY_CONSOLE_MESSAGE;
  }
  return lines.join("\n");
}

/** Payload for ticket submit; null when nothing was captured. */
export function readHubSupportConsoleCapturePayload(): string | null {
  if (lines.length === 0) {
    return null;
  }
  return lines.join("\n");
}

/** @deprecated Use readHubSupportConsoleCapturePreview or readHubSupportConsoleCapturePayload. */
export function readHubSupportConsoleCapture(): string {
  return readHubSupportConsoleCapturePreview();
}
